import React, { useRef, useState } from "react";
import Layout from "../components/Layout";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, Play, RefreshCcw, X } from "lucide-react";

function Stat({ label, value, sub, tone = "default" }) {
  const tones = {
    default: "border-[#262626]",
    gold: "border-[#D4AF37]/40 bg-[#D4AF37]/5",
    red: "border-red-500/40 bg-red-500/5",
    green: "border-emerald-500/40 bg-emerald-500/5",
  };
  return (
    <div className={`rounded-md border p-3.5 ${tones[tone]}`}>
      <div className="text-[10.5px] uppercase tracking-wider text-[#A3A3A3]">{label}</div>
      <div className="text-xl sm:text-2xl font-display font-bold mt-1">{value}</div>
      {sub && <div className="text-[11px] text-[#737373] mt-0.5">{sub}</div>}
    </div>
  );
}

function BackfillCommissions() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!window.confirm("Recalcular as comissões dos contratos importados? Isso preenche approved_amount nos clientes para que apareçam em Comissões. Não altera contratos, valores nem cadastros.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/import/backfill-commissions");
      setResult(data);
      toast.success(`Backfill: ${data.fixed} cliente(s) atualizado(s)`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha no backfill");
    } finally { setRunning(false); }
  };

  return (
    <div className="mt-4 p-3 rounded-md border border-[#262626] bg-[#0f0f0f] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 max-w-3xl">
      <div className="text-[12.5px] text-[#A3A3A3]">
        <strong className="text-white">Recalcular comissões dos contratos importados.</strong>
        <span className="block text-[11.5px] text-[#737373] mt-0.5">
          Use uma vez se notar que clientes vindos da importação não aparecem na tela de Comissões. Idempotente: ignora os que já estão corretos.
        </span>
      </div>
      <button
        onClick={run}
        disabled={running}
        className="fc-btn-ghost flex items-center gap-2 text-[12px] py-2 px-3 whitespace-nowrap"
        data-testid="backfill-commissions-btn"
      >
        {running ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
        {running ? "Recalculando..." : "Recalcular comissões"}
      </button>
      {result && (
        <div className="text-[11px] text-emerald-300 whitespace-nowrap">
          {result.fixed} atualizado(s) · {result.already_ok} OK · {result.no_contract} sem contrato · {result.no_indicator} sem indicador
        </div>
      )}
    </div>
  );
}

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setFile(null); setReport(null); setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const upload = async (f) => {
    if (!f) return;
    setFile(f); setReport(null); setResult(null); setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      toast.success(`Pré-validação: ${data.importable} de ${data.total_rows} linhas prontas`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao processar planilha");
    } finally { setLoading(false); }
  };

  const execute = async () => {
    if (!file || !report || report.importable === 0) return;
    const msg = `Confirma importar ${report.importable} cliente(s) e ${report.importable} contrato(s)?` +
      (report.with_errors > 0 ? `\n(${report.with_errors} linha(s) com erro serão puladas)` : "");
    if (!window.confirm(msg)) return;
    setExecuting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/execute", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Importação concluída: ${data.created_clients} cliente(s), ${data.created_contracts} contrato(s)`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha na importação");
    } finally { setExecuting(false); }
  };

  return (
    <Layout>
      <div className="mb-7">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Ferramenta administrativa</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Importação rápida</h1>
        <p className="text-[13px] text-[#A3A3A3] mt-2 max-w-2xl">
          Suba uma planilha <code className="text-[#D4AF37]">.xlsx</code> com as colunas <span className="text-white">NOME · SOCIO · INDICADOR · CAPITAL · JUROS · DIA DO VENCIMENTO</span>. O sistema valida os sócios/indicadores contra o banco do <strong>ambiente atual</strong> e só importa após sua confirmação.
        </p>
        <BackfillCommissions />
      </div>

      {/* Upload */}
      {!report && (
        <div className="fc-card p-6 sm:p-8 max-w-2xl">
          <label
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-md py-10 px-6 cursor-pointer transition-colors ${loading ? "opacity-60" : "hover:border-[#D4AF37]/40"} border-[#3a3a3a] bg-[#0f0f0f]`}
            data-testid="upload-area"
          >
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              disabled={loading}
              onChange={(e) => upload(e.target.files?.[0])}
            />
            {loading ? (
              <>
                <Loader2 size={28} className="text-[#D4AF37] animate-spin" />
                <div className="text-sm text-[#A3A3A3] mt-3">Processando planilha...</div>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                  <FileSpreadsheet className="text-[#D4AF37]" size={22} />
                </div>
                <div className="mt-4 font-medium">Clique para selecionar a planilha</div>
                <div className="text-[12px] text-[#737373] mt-1">XLSX, até 10 MB</div>
              </>
            )}
          </label>
        </div>
      )}

      {/* Relatório de pré-validação */}
      {report && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[12px] text-[#A3A3A3]">
              Arquivo: <span className="text-white">{file?.name}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={reset} className="fc-btn-ghost text-[12px] py-1.5 px-3 flex items-center gap-1.5" data-testid="reset-import">
                <RefreshCcw size={12} /> Trocar arquivo
              </button>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Linhas totais" value={report.total_rows} />
            <Stat label="Prontas p/ importar" value={report.importable} tone="green" />
            <Stat label="Com erro" value={report.with_errors} tone={report.with_errors > 0 ? "red" : "default"} />
            <Stat label="Capital total" value={formatBRL(report.capital_total)} tone="gold" sub={`Juros: ${formatBRL(report.juros_total)}`} />
          </div>

          {/* Match Sócios */}
          <div className="fc-card p-4 sm:p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] mb-3">Sócios — match com a base</div>
            {report.socio_matches.length === 0 && report.socio_missing.length === 0 ? (
              <div className="text-[12px] text-[#737373]">Nenhum sócio referenciado na planilha.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {report.socio_matches.map((m, i) => (
                  <div key={`sm-${i}`} className="text-[13px] flex items-center gap-2 p-2 rounded bg-[#0f0f0f] border border-[#1f1f1f]">
                    <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />
                    <span className="text-white">{m.planilha}</span>
                    <span className="text-[#737373]">→</span>
                    <span className="text-[#D4AF37]">{m.base}</span>
                  </div>
                ))}
                {report.socio_missing.map((s, i) => (
                  <div key={`sx-${i}`} className="text-[13px] flex items-center gap-2 p-2 rounded bg-red-500/5 border border-red-500/20">
                    <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
                    <span className="text-white">{s}</span>
                    <span className="text-red-300 text-[11.5px]">não existe no banco</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Match Indicadores */}
          <div className="fc-card p-4 sm:p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] mb-3">Indicadores — match com a base</div>
            {report.indicator_matches.length === 0 && report.indicator_missing.length === 0 ? (
              <div className="text-[12px] text-[#737373]">Nenhum indicador referenciado na planilha.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {report.indicator_matches.map((m, i) => (
                  <div key={`im-${i}`} className="text-[13px] flex items-center gap-2 p-2 rounded bg-[#0f0f0f] border border-[#1f1f1f]">
                    <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />
                    <span className="text-white">{m.planilha}</span>
                    <span className="text-[#737373]">→</span>
                    <span className="text-[#D4AF37]">{m.base}</span>
                  </div>
                ))}
                {report.indicator_missing.map((s, i) => (
                  <div key={`ix-${i}`} className="text-[13px] flex items-center gap-2 p-2 rounded bg-red-500/5 border border-red-500/20">
                    <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
                    <span className="text-white">{s}</span>
                    <span className="text-red-300 text-[11.5px]">não existe no banco</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detalhe linha-a-linha */}
          <div className="fc-card p-0 overflow-hidden">
            <div className="px-4 py-3 sm:px-5 sm:py-4 border-b border-[#1f1f1f]">
              <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Detalhe por linha</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-[#737373] text-left">
                    <th className="px-3 py-2">LN</th>
                    <th className="px-3 py-2">Cliente</th>
                    <th className="px-3 py-2">Sócio</th>
                    <th className="px-3 py-2">Indicador</th>
                    <th className="px-3 py-2 text-right">Capital</th>
                    <th className="px-3 py-2 text-right">% Juros</th>
                    <th className="px-3 py-2 text-right">Juros R$</th>
                    <th className="px-3 py-2">Vencimento</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.line} className={`border-t border-[#1a1a1a] ${r.ok ? "" : "bg-red-500/[0.04]"}`} data-testid={`row-${r.line}`}>
                      <td className="px-3 py-2 text-[#737373]">{r.line}</td>
                      <td className="px-3 py-2">{r.full_name || <em className="text-red-300">vazio</em>}</td>
                      <td className="px-3 py-2">
                        {r.socio_resolved ? (
                          <span><span className="text-[#A3A3A3]">{r.socio_label}</span> <span className="text-[#737373]">→</span> <span className="text-[#D4AF37]">{r.socio_resolved}</span></span>
                        ) : (
                          <span className="text-red-300">{r.socio_label || "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.indicator_resolved ? (
                          <span><span className="text-[#A3A3A3]">{r.indicator_label}</span> <span className="text-[#737373]">→</span> <span className="text-[#D4AF37]">{r.indicator_resolved}</span></span>
                        ) : (
                          <span className="text-red-300">{r.indicator_label || "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">{formatBRL(r.capital)}</td>
                      <td className="px-3 py-2 text-right">{r.interest_percent}%</td>
                      <td className="px-3 py-2 text-right">{formatBRL(r.juros_valor)}</td>
                      <td className="px-3 py-2">{r.due_date || <em className="text-red-300">—</em>}</td>
                      <td className="px-3 py-2">
                        {r.ok ? (
                          <span className="badge-base bg-emerald-500/10 border-emerald-500/30 text-emerald-300 text-[10.5px]"><CheckCircle2 size={10}/> Pronto</span>
                        ) : (
                          <span className="badge-base bg-red-500/10 border-red-500/30 text-red-300 text-[10.5px]" title={r.problems.join("; ")}><AlertTriangle size={10}/> Erro</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Ação */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-[12px] text-[#A3A3A3]">
              {report.importable > 0 ? (
                <>Pronto para importar <strong className="text-white">{report.importable}</strong> linha(s). {report.with_errors > 0 && <>{report.with_errors} linha(s) serão puladas.</>}</>
              ) : (
                <span className="text-red-300">Nenhuma linha está pronta para importar. Corrija os erros acima.</span>
              )}
            </div>
            <button
              onClick={execute}
              disabled={executing || report.importable === 0}
              className="fc-btn-gold flex items-center justify-center gap-2 text-sm py-2.5 px-5 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="execute-import"
            >
              {executing ? <Loader2 size={14} className="animate-spin"/> : <Play size={14}/>}
              {executing ? "Importando..." : "Confirmar e importar"}
            </button>
          </div>

          {/* Resultado */}
          {result && (
            <div className="fc-card p-5 border-emerald-500/30">
              <div className="text-[10.5px] uppercase tracking-wider text-emerald-300 mb-1.5">Importação concluída</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Clientes criados" value={result.created_clients} tone="green"/>
                <Stat label="Contratos criados" value={result.created_contracts} tone="green"/>
                <Stat label="Puladas" value={result.skipped}/>
                <Stat label="Capital importado" value={formatBRL(result.capital_total)} tone="gold"/>
              </div>
              {result.errors?.length > 0 && (
                <details className="mt-3 text-[12px] text-[#A3A3A3]">
                  <summary className="cursor-pointer">Linhas puladas ({result.errors.length})</summary>
                  <ul className="mt-2 space-y-1 pl-4 list-disc">
                    {result.errors.map((e, i) => (
                      <li key={i}>Linha {e.line} — {e.name || "(sem nome)"}: {e.reasons.join("; ")}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
